import React, { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import ReactSelect from 'react-select';
import {
  useCreateVoyageCharterUpdateMutation,
  useVoyageCharterContractsQuery,
  useVoyageCharterShipownerNonFinancialQuery,
  useVoyageCharterUpdateFormQuery,
} from '../../graphql';
import { nodesToOptions, reactSelectStyles } from '../../lib/form-util';
import { assertDefined, isDefined } from '../../lib/util';
import {
  Box,
  Button,
  DatePicker,
  Form,
  FormField,
  FormRow,
  FormSection,
} from '../ui';
import * as zod from 'zod';
import { toast } from 'react-toastify';
import { useCurrentUser } from '../../state/zustand';

const _voyageCharterUpdateSchema = zod.object({
  vesselId: zod.number().nullable(),
  layday: zod.string().nullable(),
  cancelling: zod.string().nullable(),
});

type VoyageCharterUpdateSchema = zod.infer<typeof _voyageCharterUpdateSchema>;

interface VoyageCharterUpdateRequestFormProps {
  onSave: () => void;
  voyageCharterId: number;
  isBorderBottom: boolean;
}

export const VoyageCharterUpdateRequestForm = ({
  onSave,
  voyageCharterId,
  isBorderBottom,
}: VoyageCharterUpdateRequestFormProps) => {
  const currentUser = useCurrentUser();
  assertDefined(currentUser, 'user should be logged in');

  const { data: voyageCharterData } =
    useVoyageCharterShipownerNonFinancialQuery({
      variables: {
        voyageCharterId,
      },
    });
  const voyageCharterNonFinancial =
    voyageCharterData?.allVwVoyageCharterShipownerNonfinancials?.nodes[0];

  const coaDataResponse = useVoyageCharterContractsQuery({
    variables: {
      voyageCharterId,
    },
  });

  const coaData = coaDataResponse?.data;

  // This feature is currently only meant for child shipowners for now (for example Navalis under Stora enso)
  // as the inter-relations between different Root organisation hasn't been developed yet.
  // So we don't want the UI component “VoyageCharterUpdateForm” to render for shipowner root orgs for now
  // (as they don't need to request a change for themselves).
  const isFormVisible =
    voyageCharterNonFinancial &&
    voyageCharterNonFinancial.shipownerOrgId == currentUser.organizationId &&
    currentUser.rootOrganization &&
    currentUser.rootOrganization.id !== currentUser.organizationId;

  const shipownerOrgId = voyageCharterNonFinancial?.shipownerOrgId ?? null;
  const coaId = coaData?.allVoyageCharterToContracts?.nodes?.[0]?.coaId;

  const { data: formData, loading: formDataLoading } =
    useVoyageCharterUpdateFormQuery({
      variables: {
        shipownerOrgId: shipownerOrgId ?? NaN,
        includeCoaVessels: !!coaId,
        coaId: coaId ?? NaN,
      },
      skip: shipownerOrgId === null || !coaData,
    });

  const [createVoyageCharterUpdate, { loading: createLoading }] =
    useCreateVoyageCharterUpdateMutation();

  const loading = formDataLoading || createLoading;
  const defaultValues = useMemo(
    () => ({
      vesselId: voyageCharterNonFinancial?.vesselId ?? null,
      layday: voyageCharterNonFinancial?.layday ?? null,
      cancelling: voyageCharterNonFinancial?.cancelling ?? null,
    }),
    [voyageCharterNonFinancial],
  );

  const { control, handleSubmit, reset } = useForm<VoyageCharterUpdateSchema>({
    defaultValues,
  });
  useEffect(() => {
    // Default values are loaded asynchronously, so we need to reset the form
    reset(defaultValues);
  }, [reset, defaultValues]);

  const vessels = formData?.allShips?.nodes.filter(isDefined) ?? [];
  const coaVesselIds =
    formData?.allCoaContractShips?.nodes
      .filter(isDefined)
      .map((coaVessel) => coaVessel.shipId) ?? null;
  const vesselOptions = nodesToOptions(
    coaVesselIds
      ? vessels.filter((vessel) => coaVesselIds.includes(vessel.id))
      : vessels,
  );

  const onSubmit = async (data: VoyageCharterUpdateSchema) => {
    const vesselId =
      data.vesselId !== defaultValues.vesselId ? data.vesselId : null;
    const layday = data.layday !== defaultValues.layday ? data.layday : null;
    const cancelling =
      data.cancelling !== defaultValues.cancelling ? data.cancelling : null;

    if (vesselId === null && layday === null && cancelling === null) {
      return;
    }

    try {
      await createVoyageCharterUpdate({
        variables: {
          input: {
            voyageCharterUpdate: {
              voyageCharterId: voyageCharterId,
              vesselId,
              layday,
              cancelling,
            },
          },
        },
      });
    } catch (err) {
      toast.error('Error creating voyage charter update');
      console.error('Error creating voyage charter update', err.message);
    }

    reset(defaultValues);
    onSave();
  };

  if (!isFormVisible) {
    return null;
  }

  return (
    <Box
      bg="neutral.white_100"
      borderColor="neutral.gray_225"
      borderWidth={1}
      borderStyle="solid"
      borderBottomWidth={isBorderBottom ? 1 : 0}
      data-testid="voyage-charter-update-form"
    >
      <Form onSubmit={handleSubmit(onSubmit)} bg="transparent" border="none">
        <FormSection borderBottomWidth={0}>
          <FormRow>
            <FormField label="Vessel" data-testid="vessel-dropdown" width={200}>
              <Controller
                name="vesselId"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <ReactSelect<(typeof vesselOptions)[number]>
                    styles={reactSelectStyles}
                    options={vesselOptions}
                    onChange={(val) => onChange(val?.value)}
                    value={vesselOptions.find((opt) => opt.value === value)}
                  />
                )}
              />
            </FormField>
            <FormField label="Layday" data-testid="layday-input" width={200}>
              <Controller
                defaultValue={null}
                control={control}
                name="layday"
                render={({ field: { value, onChange } }) => (
                  <DatePicker
                    showTime
                    showTimeInput
                    onChange={(d) =>
                      d instanceof Date ? onChange(d.toISOString()) : null
                    }
                    selected={value ? new Date(value) : null}
                    defaultTimePickerType="START_OF_DAY"
                    timeZone="UTC"
                  />
                )}
              />
            </FormField>
            <FormField label="Cancelling" width={200}>
              <Controller
                defaultValue={null}
                control={control}
                name="cancelling"
                render={({ field: { value, onChange } }) => (
                  <DatePicker
                    showTime
                    showTimeInput
                    onChange={(d) =>
                      d instanceof Date ? onChange(d.toISOString()) : null
                    }
                    selected={value ? new Date(value) : null}
                    defaultTimePickerType="END_OF_DAY"
                    timeZone="UTC"
                  />
                )}
              />
            </FormField>
            <FormField width={200}>
              <Button
                variant="secondary"
                onPress={handleSubmit(onSubmit)}
                disabled={loading}
              >
                Request changes
              </Button>
            </FormField>
          </FormRow>
        </FormSection>
      </Form>
    </Box>
  );
};
